import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import CreateUserModal from '../Modals/CreateUserModal';
import CreateRoleAssignmentModal from '../Modals/CreateRoleAssignmentModal';
import EditRoleAssignmentModal from '../Modals/EditRoleAssignmentModal';
import EditUserModal from '../Modals/EditUserModal';
import RecordDetailsModal from '../shared/RecordDetailsModal';
import { useNotifications } from '../../../context/NotificationContext';
import { institutionApi } from '../../../api/institutionsApi';
import { userApi } from '../../../api/usersApi';
import { rolesApi } from '../../../api/rolesApi';
import { roleAssignmentsApi } from '../../../api/roleAssignmentsApi';
import { setCached } from '../../../api/tabCache';
import { formatModuleScopeLabel } from '../shared/ModuleScopeField';
import { resolveUserScope } from '../shared/userScope';
import SystemRowMenu, { standardRowItems, confirmAction } from '../shared/SystemRowMenu';
import { anchorRect } from '../../shared/useAnchoredMenu';

const SUBTABS = [
  { key: 'list', label: 'Users' },
  { key: 'assignments', label: 'User Role Assignments' },
];

function fmtDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

function textOrDash(value) {
  return value ? String(value) : '-';
}

function interactiveStatusHtml(data, type) {
  if (type !== 'display') return data ? 'Active' : 'Inactive';
  const active = Boolean(data);
  const label = active ? 'Active' : 'Inactive';
  const style = active
    ? 'background:#dcfce7;color:#15803d;border:1px solid #86efac;'
    : 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;';
  return `<button class="nlims-action-btn" data-action="toggle-status" style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;${style}">${label}</button>`;
}

function readonlyStatusHtml(data, type) {
  if (type !== 'display') return data ? 'Active' : 'Inactive';
  const active = Boolean(data);
  const label = active ? 'Active' : 'Inactive';
  const style = active
    ? 'background:#dcfce7;color:#15803d;border:1px solid #86efac;'
    : 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;';
  return `<span class="inline-flex px-3 py-0.5 rounded-md text-[11px] font-semibold" style="${style}">${label}</span>`;
}

function userActionHtml() {
  return `<button type="button" class="nlims-action-btn" data-action="menu" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:3px 12px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;">&#8943;</button>`;
}

function assignmentActionHtml() {
  return `<button type="button" class="nlims-action-btn" data-action="menu" aria-label="Row actions" title="More actions" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:3px 12px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;">&#8943;</button>`;
}

const USER_COLS = [
  { title: 'Name',        data: 'name',             sortable: true,  render: (d, t) => (t === 'display' ? textOrDash(d) : (d ?? '')) },
  { title: 'Email',       data: 'email',            sortable: true,  render: (d, t) => (t === 'display' ? textOrDash(d) : (d ?? '')) },
  { title: 'Institution', data: 'institution_label', sortable: true, render: (d, t) => (t === 'display' ? textOrDash(d) : (d ?? '')) },
  { title: 'Status',      data: 'is_active',        sortable: true,  render: interactiveStatusHtml },
  { title: 'Last Login',  data: 'last_login',       sortable: true,  render: (d, t) => (t === 'display' ? fmtDate(d) : (d ?? '')) },
  { title: 'Action',      data: null,               sortable: false, render: (_d, _t, row) => userActionHtml(row) },
];

const ASSIGN_COLS = [
  { title: 'User', data: 'user_label', sortable: true, render: (d, t) => (t === 'display' ? textOrDash(d) : (d ?? '')) },
  { title: 'Role', data: 'role_label', sortable: true, render: (d, t) => (t === 'display' ? textOrDash(d) : (d ?? '')) },
  { title: 'Institution Scope', data: 'institution_label', sortable: true, render: (d, t) => (t === 'display' ? textOrDash(d) : (d ?? '')) },
  { title: 'Module Scope', data: 'module_scope', sortable: true, render: (d, t) => (t === 'display' ? formatModuleScopeLabel(d) : (d ?? '')) },
  { title: 'Expires', data: 'expires_at', sortable: true, render: (d, t) => (t === 'display' ? fmtDate(d) : (d ?? '')) },
  { title: 'Status', data: 'is_active', sortable: true, render: readonlyStatusHtml },
  { title: 'Action', data: null, sortable: false, render: () => assignmentActionHtml() },
];

function SearchCreateRow({ search, setSearch, onSearch, onCreate, createLabel = 'Create New' }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="relative flex-1">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          placeholder="Search here"
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        />
      </div>
      <button
        onClick={onSearch}
        className="px-5 py-2.5 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
      >
        Search
      </button>
      <button
        onClick={onCreate}
        className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800"
      >
        <span className="text-base font-bold leading-none">+</span> {createLabel}
      </button>
    </div>
  );
}

export default function UserManager() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { refreshNotifications } = useNotifications();
  const sub = pathname.split('/').filter(Boolean)[2] ?? 'list';

  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [uSearch, setUS] = useState('');
  const [uSQ, setUSQ] = useState('');
  const [userModal, setUM] = useState(false);
  const [editUser, setEU] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { kind: 'user' | 'assignment', row, rect }
  const [deletingUser, setDeletingUser] = useState(false);

  const [aSearch, setAS] = useState('');
  const [aSQ, setASQ] = useState('');
  const [assignModal, setAM] = useState(false);
  const [viewAssign, setViewAssign] = useState(null);
  const [editAssign, setEditAssign] = useState(null);
  const [deletingAssign, setDeletingAssign] = useState(false);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const results = await Promise.allSettled([
        institutionApi.list({ per_page: 1000, page: 1 }),
        userApi.list({ per_page: 1000, page: 1 }),
        rolesApi.list(),
        roleAssignmentsApi.list({ per_page: 200, page: 1 }),
      ]);

      const [institutionsResult, usersResult, rolesResult, assignmentsResult] = results;
      const failures = results.filter((result) => result.status === 'rejected');

      if (institutionsResult.status === 'fulfilled') {
        setInstitutions(Array.isArray(institutionsResult.value) ? institutionsResult.value : []);
      }

      if (usersResult.status === 'fulfilled') {
        const liveUsers = Array.isArray(usersResult.value) ? usersResult.value : [];
        setUsers(liveUsers);
        setCached('users', liveUsers);
      }

      if (rolesResult.status === 'fulfilled') {
        setRoles(Array.isArray(rolesResult.value) ? rolesResult.value : []);
      }

      if (assignmentsResult.status === 'fulfilled') {
        const liveAssignments = Array.isArray(assignmentsResult.value) ? assignmentsResult.value : [];
        const liveUsers = usersResult.status === 'fulfilled' && Array.isArray(usersResult.value) ? usersResult.value : [];
        const liveInstitutions = institutionsResult.status === 'fulfilled' && Array.isArray(institutionsResult.value) ? institutionsResult.value : [];

        // Enrich assignments: backend returns only UUIDs, not nested names
        const userMap = Object.fromEntries(
          liveUsers.map((u) => [u.uuid ?? u.id, u.name || u.email || u.uuid])
        );
        const instMap = Object.fromEntries(
          liveInstitutions.map((i) => [i.uuid ?? i.id, i.name || i.uuid])
        );
        setAssignments(
          liveAssignments.map((a) => ({
            ...a,
            user_label: userMap[a.user_uuid] || a.user_label || a.user_uuid || '—',
            institution_label: a.institution_uuid
              ? (instMap[a.institution_uuid] || a.institution_label || a.institution_uuid)
              : 'All institutions',
          }))
        );
      }

      if (failures.length === results.length) {
        const msg =
          failures[0]?.reason?.response?.data?.message ||
          failures[0]?.reason?.response?.data?.error ||
          failures[0]?.reason?.message ||
          'Unable to load user management data.';
        setLoadError(msg);
        toast.error(msg, { position: 'top-right' });
      } else {
        setLoadError('');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  const handleSaveUser = async (payload) => {
    await userApi.update(payload.uuid, payload);
    await reloadAll();
  };

  const handleCreateUser = async (payload) => {
    await userApi.create(payload);
    await reloadAll();
  };

  const handleDeleteUser = async (row) => {
    if (!row?.uuid) return;
    if (!(await confirmAction({ title: 'Delete Record?', message: `Delete ${row.name || row.email || 'this user'}?`, confirmLabel: 'Delete', danger: true }))) return;
    setDeletingUser(true);
    try {
      await userApi.remove(row.uuid);
      setViewUser(null);
      await reloadAll();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to delete user.', { position: 'top-right' });
    } finally {
      setDeletingUser(false);
    }
  };

  const handleUserAction = async (action, row, meta) => {
    if (action === 'menu') { setRowMenu({ kind: 'user', row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'view') { setViewUser(row); return; }
    if (action === 'edit') {
      setEU(row);
      return;
    }
    if (action === 'toggle-status') {
      const nextLabel = row.is_active ? 'Deactivate' : 'Activate';
      if (!(await confirmAction({ title: `${nextLabel} User?`, message: `${row.name || row.email || 'This user'} will be marked ${row.is_active ? 'Inactive' : 'Active'}.`, confirmLabel: nextLabel }))) return;
      try {
        await userApi.update(row.uuid, {
          institution_uuid: row.institution_uuid ?? row.institution_id,
          role_id: row.role_id ?? row.roleId ?? null,
          role_code: row.role_code ?? row.roleCode ?? null,
          state: row.state,
          lga: row.lga,
          is_active: !row.is_active,
        });
        await reloadAll();
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Unable to update user status.';
        toast.error(msg, { position: 'top-right' });
      }
      return;
    }
    if (action === 'delete') { await handleDeleteUser(row); }
  };

  const handleCreateAssign = async (payload) => {
    await roleAssignmentsApi.create(payload);
    await reloadAll();
    refreshNotifications();
  };

  const handleSaveAssignment = async (payload) => {
    if (!payload?.uuid) {
      throw new Error('Missing assignment identifier.');
    }
    await roleAssignmentsApi.update(payload.uuid, payload);
    await reloadAll();
    refreshNotifications();
  };

  const handleDeleteAssignment = async (row) => {
    if (!row?.uuid) return;
    const label = `${row.user_label || row.user_uuid || 'this user'} / ${row.role_label || row.role_code || 'this role'}`;
    if (!(await confirmAction({ title: 'Delete Record?', message: `Delete assignment for ${label}?`, confirmLabel: 'Delete', danger: true }))) return;

    setDeletingAssign(true);
    try {
      await roleAssignmentsApi.remove(row.uuid);
      setViewAssign(null);
      toast.success('Role assignment deleted.', { position: 'top-right' });
      await reloadAll();
      refreshNotifications();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Unable to delete role assignment.';
      toast.error(msg, { position: 'top-right' });
    } finally {
      setDeletingAssign(false);
    }
  };

  const handleAssignmentAction = async (action, row, meta) => {
    if (action === 'menu') { setRowMenu({ kind: 'assignment', row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'view') { setViewAssign(row); return; }
    if (action === 'edit') { setEditAssign(row); return; }
    if (action === 'delete') { await handleDeleteAssignment(row); }
  };

  const showInitialLoading = loading && users.length === 0 && assignments.length === 0 && roles.length === 0 && institutions.length === 0;
  const viewUserScope = resolveUserScope(viewUser ?? {});
  const viewUserState = viewUserScope.isNationalScope
    ? 'All States'
    : (viewUser?.state || '—');
  const viewUserLga = viewUserScope.isNationalScope
    ? 'All LGAs'
    : (viewUser?.lga || '—');

  if (loadError && users.length === 0 && assignments.length === 0 && roles.length === 0 && institutions.length === 0) {
    return (
      <section className="rounded-xl border border-red-100 bg-red-50 px-6 py-10 text-sm text-red-700">
        {loadError}
      </section>
    );
  }

  return (
    <section>
      <div className="mb-5 flex flex-wrap gap-3">
        {SUBTABS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => navigate(`/admin/users/${s.key}`)}
            className={`rounded-lg border px-4 py-2 text-sm font-medium ${
              sub === s.key
                ? 'border-green-100 bg-green-50 font-semibold text-green-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-green-500'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sub === 'list' ? (
        <>
          <SearchCreateRow
            search={uSearch}
            setSearch={setUS}
            onSearch={() => setUSQ(uSearch)}
            onCreate={() => setUM(true)}
          />
          {!showInitialLoading && (
            <NlimsDataTable
              key="users-tbl"
              tableId="users-table"
              columns={USER_COLS}
              data={users}
              serverSide={false}
              searchQuery={uSQ}
              hideSearch
              pageLength={10}
              enableRowSelection
              onRowAction={handleUserAction}
            />
          )}
          {rowMenu?.kind === 'user' && (
            <SystemRowMenu
              rect={rowMenu.rect}
              items={standardRowItems({ isActive: Boolean(rowMenu.row?.is_active) })}
              onClose={() => setRowMenu(null)}
              onPick={(key) => handleUserAction(key, rowMenu.row)}
            />
          )}
          <CreateUserModal
            isOpen={userModal}
            onClose={() => setUM(false)}
            onCreate={handleCreateUser}
            institutions={institutions}
            existingRows={users}
          />
          <EditUserModal
            isOpen={!!editUser}
            onClose={() => setEU(null)}
            onSave={handleSaveUser}
            row={editUser}
            institutions={institutions}
            existingRows={users}
          />
          <RecordDetailsModal
            isOpen={!!viewUser}
            onClose={() => setViewUser(null)}
            onEdit={() => { setEU(viewUser); setViewUser(null); }}
            onDelete={() => handleDeleteUser(viewUser)}
            deleting={deletingUser}
            title="User Details"
            editLabel="Edit User"
            subtitle={viewUser?.name || viewUser?.email}
            statusValue={viewUser?.is_active ? 'active' : 'inactive'}
            fields={viewUser ? [
              { label: 'Name',        value: viewUser.name || '—' },
              { label: 'Email',       value: viewUser.email || '—' },
              { label: 'Institution', value: viewUser.institution_label || '—' },
              { label: 'State',       value: viewUserState },
              { label: 'LGA',         value: viewUserLga },
              { label: 'Status',      value: viewUser.is_active ? 'Active' : 'Inactive' },
              { label: 'Last Login',  value: viewUser.last_login ? fmtDate(viewUser.last_login) : '—' },
              { label: 'UUID',        value: viewUser.uuid, mono: true, wide: true },
            ] : []}
          />
        </>
      ) : (
        <>
          <SearchCreateRow
            search={aSearch}
            setSearch={setAS}
            onSearch={() => setASQ(aSearch)}
            onCreate={() => setAM(true)}
            createLabel="Assign Role"
          />
          {!showInitialLoading && (
            <NlimsDataTable
              key="assignments-tbl"
              tableId="assignments-table"
              columns={ASSIGN_COLS}
              data={assignments}
              serverSide={false}
              searchQuery={aSQ}
              hideSearch
              pageLength={10}
              enableRowSelection
              onRowAction={handleAssignmentAction}
            />
          )}
          {rowMenu?.kind === 'assignment' && (
            <SystemRowMenu
              rect={rowMenu.rect}
              items={standardRowItems()}
              onClose={() => setRowMenu(null)}
              onPick={(key) => handleAssignmentAction(key, rowMenu.row)}
            />
          )}
          <CreateRoleAssignmentModal
            isOpen={assignModal}
            onClose={() => setAM(false)}
            onCreate={handleCreateAssign}
            users={users}
            roles={roles}
            institutions={institutions}
          />
          <RecordDetailsModal
            isOpen={!!viewAssign}
            onClose={() => setViewAssign(null)}
            onEdit={() => { setEditAssign(viewAssign); setViewAssign(null); }}
            title="Role Assignment Details"
            editLabel="Edit Assignment"
            subtitle={viewAssign ? `${viewAssign.user_label || viewAssign.user_uuid || 'User'} / ${viewAssign.role_label || viewAssign.role_code || 'Role'}` : undefined}
            statusValue={viewAssign?.is_active ? 'active' : 'inactive'}
            onDelete={() => handleDeleteAssignment(viewAssign)}
            deleting={deletingAssign}
            deleteLabel="Delete Assignment"
            fields={viewAssign ? [
              { label: 'User', value: viewAssign.user_label || '—' },
              { label: 'User UUID', value: viewAssign.user_uuid || '—', mono: true, wide: true },
              { label: 'Role', value: viewAssign.role?.name || viewAssign.role_label || '—' },
              { label: 'Role Code', value: viewAssign.role?.code || viewAssign.role_code || '—' },
              { label: 'Role UUID', value: viewAssign.role?.uuid || viewAssign.role_uuid || '—', mono: true, wide: true },
              { label: 'Institution Scope', value: viewAssign.institution_label || 'All institutions' },
              { label: 'Institution UUID', value: viewAssign.institution_uuid || '—', mono: true, wide: true },
              { label: 'Module Scope', value: formatModuleScopeLabel(viewAssign.module_scope) },
              { label: 'Expires At', value: viewAssign.expires_at ? fmtDate(viewAssign.expires_at) : '—' },
              { label: 'Assigned By UUID', value: viewAssign.assigned_by_uuid || viewAssign.assigned_by || '—', mono: true, wide: true },
              { label: 'Created At', value: fmtDate(viewAssign.created_at) },
              { label: 'Updated At', value: fmtDate(viewAssign.updated_at) },
            ] : []}
          />
          <EditRoleAssignmentModal
            isOpen={!!editAssign}
            onClose={() => setEditAssign(null)}
            onSave={handleSaveAssignment}
            row={editAssign}
            institutions={institutions}
            roles={roles}
          />
        </>
      )}
    </section>
  );
}
